import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Plus, 
  Search, 
  Users, 
  Building2, 
  User, 
  Crown,
  UserPlus,
  Filter,
  Phone,
  Mail,
  MapPin,
  MoreVertical,
  Pencil,
  Trash2,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useAdminLanguage } from '@/context/AdminLanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminCustomers() {
  const { t } = useAdminLanguage();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [stats, setStats] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  const fetchCustomers = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      if (typeFilter) params.append('customer_type', typeFilter);
      
      const res = await fetch(`${API_URL}/api/customers?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCustomers(data);
      } else {
        console.error('Error fetching customers:', res.status);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
      toast.error(t('msg_error'));
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/customers/stats/overview`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      } else {
        console.error('Error fetching stats:', res.status);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  useEffect(() => {
    fetchCustomers();
    fetchStats();
  }, [search, statusFilter, typeFilter]);

  const handleDelete = async () => {
    if (!deleteId) return;
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/customers/${deleteId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success(t('msg_delete_success'));
        fetchCustomers();
        fetchStats();
      } else {
        toast.error(t('msg_delete_error'));
      }
    } catch (error) {
      toast.error(t('msg_delete_error'));
    } finally {
      setDeleteId(null);
    }
  };

  const getStatusBadge = (status) => {
    const isEnglish = t('customers_title') === 'Customers';
    const styles = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-gray-100 text-gray-800',
      vip: 'bg-amber-100 text-amber-800',
      prospect: 'bg-blue-100 text-blue-800'
    };
    const labels = {
      active: isEnglish ? 'Active' : 'Aktiv',
      inactive: isEnglish ? 'Inactive' : 'Inaktiv',
      vip: 'VIP',
      prospect: isEnglish ? 'Prospect' : 'Interessent'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.active}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getTypeBadge = (type) => {
    const isEnglish = t('customers_title') === 'Customers';
    if (type === 'business') {
      return (
        <span className="flex items-center gap-1 text-xs text-purple-700">
          <Building2 size={12} />
          {isEnglish ? 'Business' : 'Geschäftlich'}
        </span>
      );
    }
    return (
      <span className="flex items-center gap-1 text-xs text-gray-600">
        <User size={12} />
        {isEnglish ? 'Private' : 'Privat'}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-heading" data-testid="customers-title">{t('customers_title')}</h1>
          <p className="text-sm text-[#666666] mt-1">
            {stats?.total || 0} {t('customers_title') === 'Customers' ? 'customers total' : 'Kunden insgesamt'}
          </p>
        </div>
        <Link to="/admin/customers/new">
          <Button className="btn-primary flex items-center gap-2" data-testid="add-customer-btn">
            <Plus size={18} />
            {t('customers_add_new')}
          </Button>
        </Link>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <div className="bg-white p-4 border border-[#E5E5E5] rounded-lg">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users size={20} className="text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-heading">{stats.total}</p>
                <p className="text-xs text-[#666666]">{t('customers_title') === 'Customers' ? 'Total' : 'Gesamt'}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 border border-[#E5E5E5] rounded-lg">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <User size={20} className="text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-heading">{stats.active}</p>
                <p className="text-xs text-[#666666]">{t('status_active')}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 border border-[#E5E5E5] rounded-lg">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Crown size={20} className="text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-heading">{stats.vip}</p>
                <p className="text-xs text-[#666666]">VIP</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 border border-[#E5E5E5] rounded-lg">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <UserPlus size={20} className="text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-heading">{stats.prospects}</p>
                <p className="text-xs text-[#666666]">{t('customers_title') === 'Customers' ? 'Prospects' : 'Interessenten'}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 border border-[#E5E5E5] rounded-lg">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Building2 size={20} className="text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-heading">{stats.business}</p>
                <p className="text-xs text-[#666666]">{t('customers_title') === 'Customers' ? 'Business' : 'Geschäftlich'}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 border border-[#E5E5E5] rounded-lg">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <UserPlus size={20} className="text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-heading">{stats.recent}</p>
                <p className="text-xs text-[#666666]">{t('customers_title') === 'Customers' ? 'New (30 days)' : 'Neu (30 Tage)'}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search & Filters */}
      <div className="bg-white p-4 border border-[#E5E5E5] rounded-lg">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#999999]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Suchen nach Name, E-Mail, Telefon, Stadt..."
              className="input-premium w-full pl-10"
              data-testid="customer-search"
            />
          </div>
          <Button 
            onClick={() => setShowFilters(!showFilters)}
            className="btn-secondary flex items-center gap-2"
          >
            <Filter size={16} />
            Filter
          </Button>
        </div>
        
        {showFilters && (
          <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t border-[#E5E5E5]">
            <div>
              <label className="text-xs text-[#666666] block mb-1">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="input-premium text-sm"
              >
                <option value="">Alle</option>
                <option value="active">Aktiv</option>
                <option value="inactive">Inaktiv</option>
                <option value="vip">VIP</option>
                <option value="prospect">Interessent</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-[#666666] block mb-1">Typ</label>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="input-premium text-sm"
              >
                <option value="">Alle</option>
                <option value="private">Privat</option>
                <option value="business">Geschäftlich</option>
              </select>
            </div>
            {(statusFilter || typeFilter) && (
              <Button 
                onClick={() => { setStatusFilter(''); setTypeFilter(''); }}
                className="btn-ghost text-sm self-end"
              >
                Filter zurücksetzen
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Customer List */}
      <div className="bg-white border border-[#E5E5E5] rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <div className="spinner mx-auto" />
          </div>
        ) : customers.length === 0 ? (
          <div className="p-12 text-center">
            <Users size={48} className="mx-auto text-[#CCCCCC] mb-4" />
            <p className="text-[#666666]">
              {search || statusFilter || typeFilter 
                ? 'Keine Kunden gefunden' 
                : 'Noch keine Kunden vorhanden'}
            </p>
            {!search && !statusFilter && !typeFilter && (
              <Link to="/admin/customers/new" className="btn-primary inline-block mt-4">
                Ersten Kunden anlegen
              </Link>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="admin-table w-full">
              <thead>
                <tr>
                  <th>Kunde</th>
                  <th>Kontakt</th>
                  <th>Adresse</th>
                  <th>Typ</th>
                  <th>Status</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((customer) => (
                  <tr key={customer.id} data-testid={`customer-row-${customer.id}`}>
                    <td>
                      <div>
                        <p className="font-medium">
                          {customer.first_name} {customer.last_name}
                        </p>
                        <p className="text-xs text-[#999999]">{customer.customer_number}</p>
                        {customer.company && (
                          <p className="text-xs text-purple-600">{customer.company}</p>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="space-y-1">
                        {customer.email && (
                          <div className="flex items-center gap-1 text-sm">
                            <Mail size={12} className="text-[#999999]" />
                            <span>{customer.email}</span>
                          </div>
                        )}
                        {customer.phone && (
                          <div className="flex items-center gap-1 text-sm">
                            <Phone size={12} className="text-[#999999]" />
                            <span>{customer.phone}</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      {customer.city ? (
                        <div className="flex items-center gap-1 text-sm">
                          <MapPin size={12} className="text-[#999999]" />
                          <span>{customer.zip_code} {customer.city}</span>
                        </div>
                      ) : (
                        <span className="text-[#999999] text-sm">-</span>
                      )}
                    </td>
                    <td>{getTypeBadge(customer.customer_type)}</td>
                    <td>{getStatusBadge(customer.status)}</td>
                    <td>
                      <div className="flex items-center gap-1">
                        <Link
                          to={`/admin/customers/${customer.id}`}
                          className="p-2 hover:bg-[#F9F9F7] rounded transition-colors"
                          title="Anzeigen"
                        >
                          <Eye size={16} />
                        </Link>
                        <Link
                          to={`/admin/customers/${customer.id}/edit`}
                          className="p-2 hover:bg-[#F9F9F7] rounded transition-colors"
                          title="Bearbeiten"
                        >
                          <Pencil size={16} />
                        </Link>
                        <button
                          onClick={() => setDeleteId(customer.id)}
                          className="p-2 hover:bg-red-50 rounded transition-colors text-red-600"
                          title="Löschen"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
            <h3 className="font-heading text-xl mb-4">Kunde löschen?</h3>
            <p className="text-[#666666] mb-6">
              Sind Sie sicher, dass Sie diesen Kunden löschen möchten? Alle zugehörigen Dateien und Kommentare werden ebenfalls gelöscht.
            </p>
            <div className="flex gap-3 justify-end">
              <Button onClick={() => setDeleteId(null)} className="btn-secondary">
                Abbrechen
              </Button>
              <Button onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white">
                Löschen
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
